import type { ReactNode } from 'react'

import { Navigate } from 'react-router-dom'

import { useAuthStore, type Role } from '@/stores/auth.store'
import Loading from '@/pages/Loading'

interface RequireRoleProps {
  /** Every role that is permitted to view this route. */
  roles: Role[]
  /** Where to send users who fail the role check. Defaults to '/'. */
  redirectTo?: string
  children: ReactNode
}

/**
 * Route-level authorization wrapper.
 *
 * Renders children only when the authenticated user's role is in `roles`.
 * Handles all three auth states explicitly so callers don't have to:
 *
 *   permissions === null, no error → still loading  → <Loading />
 *   permissionsError === true      → fetch failed   → redirect (fail-closed)
 *   permissions.role not in roles  → wrong role     → redirect
 *   permissions.role in roles      → authorized     → children
 */
const RequireRole = ({
  roles,
  redirectTo = '/',
  children,
}: RequireRoleProps) => {
  const permissions = useAuthStore((s) => s.permissions)
  const permissionsError = useAuthStore((s) => s.permissionsError)

  if (permissions === null && !permissionsError) {
    return <Loading />
  }

  if (permissionsError || !roles.includes(permissions!.role)) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

export default RequireRole
